import { Form, Formik } from "formik";
import { Pencil } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import * as Yup from "yup";
import { FormField } from "@/components/common/form-field";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  createStandardVintage,
  listRegionCounties,
  listStandards,
  listVehicleRegions,
  type RegionCounty,
  updateVehicleRegion,
  type VehicleRegion,
} from "@/features/reference/api";
import { ApiError } from "@/lib/api";

const MONEY_RE = /^\d+(\.\d{1,2})?$/;
const money = () => Yup.string().matches(MONEY_RE, "Enter a valid amount").required("Required");

export function VehicleTab({
  effectiveOn,
  reloadToken,
}: {
  effectiveOn: string;
  reloadToken: number;
}) {
  const [regions, setRegions] = useState<VehicleRegion[]>([]);
  const [counties, setCounties] = useState<RegionCounty[]>([]);
  const [ownership, setOwnership] = useState<Record<string, string>>({});
  const [transit, setTransit] = useState<string>("—");
  const [loading, setLoading] = useState(true);
  const [editingRegion, setEditingRegion] = useState<VehicleRegion | undefined>();
  const [nationalOpen, setNationalOpen] = useState(false);

  const refetch = useCallback(() => {
    setLoading(true);
    Promise.all([
      listVehicleRegions(effectiveOn),
      listRegionCounties({}),
      listStandards({ standardType: "VEHICLE_OWNERSHIP", effectiveOn }),
      listStandards({ standardType: "PUBLIC_TRANSIT", effectiveOn }),
    ])
      .then(([r, rc, own, trans]) => {
        setRegions(r.items);
        setCounties(rc.items);
        setOwnership(Object.fromEntries(own.items.map((s) => [s.item, s.monthlyAmount])));
        setTransit(trans.items[0]?.monthlyAmount ?? "—");
      })
      .catch(() => toast.error("Couldn't load vehicle allowances."))
      .finally(() => setLoading(false));
  }, [effectiveOn]);

  // reloadToken is a deliberate extra trigger (e.g. after a JSON import).
  useEffect(() => {
    refetch();
  }, [refetch, reloadToken]);

  function countiesFor(region: string): string {
    return (
      counties
        .filter((c) => c.region === region)
        .map((c) => `${c.county}, ${c.state}`)
        .join(" · ") || "—"
    );
  }

  if (loading) return <p className="text-muted-foreground text-sm">Loading…</p>;

  return (
    <div className="grid gap-6">
      <section>
        <h3 className="mb-2 text-sm font-semibold">Operating cost by region</h3>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Region</TableHead>
                <TableHead className="text-right">One car</TableHead>
                <TableHead className="text-right">Two cars</TableHead>
                <TableHead>Counties</TableHead>
                <TableHead className="text-right">Edit</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {regions.map((r) => (
                <TableRow key={r.id}>
                  <TableCell className="font-medium">{r.region}</TableCell>
                  <TableCell className="text-right tabular-nums">{r.oneCarMonthly}</TableCell>
                  <TableCell className="text-right tabular-nums">{r.twoCarsMonthly}</TableCell>
                  <TableCell className="text-muted-foreground max-w-xs truncate text-xs">
                    {countiesFor(r.region)}
                  </TableCell>
                  <TableCell className="text-right">
                    <Button
                      size="icon-sm"
                      variant="ghost"
                      aria-label={`Edit ${r.region}`}
                      onClick={() => setEditingRegion(r)}
                    >
                      <Pencil className="size-3.5" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </section>

      <section className="grid gap-3 sm:grid-cols-2">
        <div className="rounded-lg border p-4">
          <h3 className="mb-2 text-sm font-semibold">National (ownership & transit)</h3>
          <dl className="grid grid-cols-2 gap-y-1 text-sm">
            <dt className="text-muted-foreground">Ownership · one car</dt>
            <dd className="text-right tabular-nums">{ownership.one_car ?? "—"}</dd>
            <dt className="text-muted-foreground">Ownership · two cars</dt>
            <dd className="text-right tabular-nums">{ownership.two_cars ?? "—"}</dd>
            <dt className="text-muted-foreground">Public transit</dt>
            <dd className="text-right tabular-nums">{transit}</dd>
          </dl>
          <Button size="sm" variant="outline" className="mt-3" onClick={() => setNationalOpen(true)}>
            New vintage
          </Button>
        </div>
      </section>

      {editingRegion && (
        <EditRegionDialog
          region={editingRegion}
          onClose={() => setEditingRegion(undefined)}
          onSaved={refetch}
        />
      )}
      {nationalOpen && (
        <NationalVehicleDialog
          initial={{
            oneCar: ownership.one_car ?? "",
            twoCars: ownership.two_cars ?? "",
            transit: transit === "—" ? "" : transit,
          }}
          onClose={() => setNationalOpen(false)}
          onSaved={() => {
            setNationalOpen(false);
            refetch();
          }}
        />
      )}
    </div>
  );
}

function EditRegionDialog({
  region,
  onClose,
  onSaved,
}: {
  region: VehicleRegion;
  onClose: () => void;
  onSaved: () => void;
}) {
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Edit {region.region}</DialogTitle>
          <DialogDescription>Monthly vehicle-operating allowance for this region.</DialogDescription>
        </DialogHeader>
        <Formik
          initialValues={{ oneCar: region.oneCarMonthly, twoCars: region.twoCarsMonthly }}
          validationSchema={Yup.object({ oneCar: money(), twoCars: money() })}
          onSubmit={async (values, { setSubmitting }) => {
            try {
              await updateVehicleRegion(region.id, {
                oneCarMonthly: values.oneCar,
                twoCarsMonthly: values.twoCars,
              });
              toast.success("Region updated");
              onSaved();
              onClose();
            } catch (err) {
              toast.error(err instanceof ApiError ? err.message : "Couldn't update the region.");
              setSubmitting(false);
            }
          }}
        >
          {({ isSubmitting }) => (
            <Form className="grid gap-5">
              <div className="grid grid-cols-2 gap-4">
                <FormField name="oneCar" label="One car" inputMode="decimal" />
                <FormField name="twoCars" label="Two cars" inputMode="decimal" />
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={onClose} disabled={isSubmitting}>
                  Cancel
                </Button>
                <Button type="submit" disabled={isSubmitting}>
                  {isSubmitting ? "Saving…" : "Save"}
                </Button>
              </DialogFooter>
            </Form>
          )}
        </Formik>
      </DialogContent>
    </Dialog>
  );
}

function NationalVehicleDialog({
  initial,
  onClose,
  onSaved,
}: {
  initial: { oneCar: string; twoCars: string; transit: string };
  onClose: () => void;
  onSaved: () => void;
}) {
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>New national vehicle vintage</DialogTitle>
          <DialogDescription>Ownership (one/two cars) and public transit, at a new effective date.</DialogDescription>
        </DialogHeader>
        <Formik
          initialValues={{
            effectiveDate: new Date().toISOString().slice(0, 10),
            oneCar: initial.oneCar,
            twoCars: initial.twoCars,
            transit: initial.transit,
          }}
          validationSchema={Yup.object({
            effectiveDate: Yup.string().required("Required"),
            oneCar: money(),
            twoCars: money(),
            transit: money(),
          })}
          onSubmit={async (values, { setSubmitting }) => {
            try {
              await createStandardVintage(values.effectiveDate, [
                { standardType: "VEHICLE_OWNERSHIP", item: "one_car", householdSize: null, monthlyAmount: values.oneCar },
                { standardType: "VEHICLE_OWNERSHIP", item: "two_cars", householdSize: null, monthlyAmount: values.twoCars },
                { standardType: "PUBLIC_TRANSIT", item: "national", householdSize: null, monthlyAmount: values.transit },
              ]);
              toast.success("New national vehicle vintage saved");
              onSaved();
            } catch (err) {
              toast.error(err instanceof ApiError ? err.message : "Couldn't save the vintage.");
              setSubmitting(false);
            }
          }}
        >
          {({ isSubmitting }) => (
            <Form className="grid gap-4">
              <FormField name="effectiveDate" label="Effective date" type="date" />
              <div className="grid grid-cols-3 gap-4">
                <FormField name="oneCar" label="One car" inputMode="decimal" />
                <FormField name="twoCars" label="Two cars" inputMode="decimal" />
                <FormField name="transit" label="Transit" inputMode="decimal" />
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={onClose} disabled={isSubmitting}>
                  Cancel
                </Button>
                <Button type="submit" disabled={isSubmitting}>
                  {isSubmitting ? "Saving…" : "Save vintage"}
                </Button>
              </DialogFooter>
            </Form>
          )}
        </Formik>
      </DialogContent>
    </Dialog>
  );
}
